import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { TradeGroup } from "../../types/trades";
import api from "../../api/axios";

interface TradeGroupState {
    groups: Record<string, TradeGroup>;
    loading: boolean;
    error: string | null;
    isGroupingEnabled: boolean;
}

const saved = localStorage.getItem('groupedTrades');
const initialState: TradeGroupState = {
    groups: {},
    loading: false,
    error: null,
    isGroupingEnabled: saved === null ? true : saved === 'true',
};

// Async thunk to fetch trade groups by a list of ids
export const fetchTradeGroups = createAsyncThunk(
    'tradeGroups/fetchTradeGroups',
    async (ids: string[], { rejectWithValue }) => {
        try {
            console.trace('fetchTradeGroups');

            const response = await api.get<TradeGroup[]>('/trade-groups', {
                params: { ids: ids.join(',') }
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'Failed to fetch trade groups');
        }
    }
);

const tradeGroupSlice = createSlice({
    name: 'tradeGroups',
    initialState,
    reducers: {
        // Action to manually add or update a single group without a fetch
        upsertTradeGroup: (state, action: PayloadAction<TradeGroup>) => {
            state.groups[action.payload.id] = action.payload;
        },
        // Action to clear groups if necessary
        clearTradeGroups: (state) => {
            state.groups = {};
        },
        // Toggle grouping and persist to localStorage
        toggleTradeGroups: (state) => {
            state.isGroupingEnabled = !state.isGroupingEnabled;
            localStorage.setItem('groupedTrades', String(state.isGroupingEnabled));
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTradeGroups.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTradeGroups.fulfilled, (state, action: PayloadAction<TradeGroup[]>) => {
                state.loading = false;
                action.payload.forEach((group) => {
                    state.groups[group.id] = group;
                });
            })
            .addCase(fetchTradeGroups.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    upsertTradeGroup,
    clearTradeGroups,
    toggleTradeGroups
} = tradeGroupSlice.actions;

export default tradeGroupSlice.reducer;